import { useEffect, useRef } from "react";

export default function InfinityLoop({ className }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    let t = 0;
    let tiltX = 0;
    let tiltY = 0;
    let curX = 0;
    let curY = 0;
    let targetTighten = 1;
    let curTighten = 1;
    const cursor = { x: -9999, y: -9999 };
    const introStart = performance.now();
    const INTRO = 2400;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      w = r.width;
      h = r.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const N = 110;
    const particles = [];
    for (let i = 0; i < N; i++) {
      particles.push({
        phase: (i / N) * Math.PI * 2,
        speed: 0.0022 + Math.random() * 0.002,
        r: 0.55 + Math.random() * 1.1,
        tw: Math.random() * Math.PI * 2,
        ts: 0.5 + Math.random() * 1.2,
        glow: 0,
      });
    }
    const trail = Array.from({ length: N }, () => []);

    const sprite = document.createElement("canvas");
    sprite.width = 64;
    sprite.height = 64;
    const sctx = sprite.getContext("2d");
    const grad = sctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, "rgba(150, 172, 255, 1)");
    grad.addColorStop(0.3, "rgba(100, 132, 255, 0.5)");
    grad.addColorStop(1, "rgba(60, 100, 255, 0)");
    sctx.fillStyle = grad;
    sctx.fillRect(0, 0, 64, 64);

    const bloomSprite = document.createElement("canvas");
    bloomSprite.width = 256;
    bloomSprite.height = 256;
    const bctx = bloomSprite.getContext("2d");
    const bgrad = bctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    bgrad.addColorStop(0, "rgba(70, 108, 255, 0.5)");
    bgrad.addColorStop(0.45, "rgba(55, 90, 245, 0.18)");
    bgrad.addColorStop(1, "rgba(40, 70, 230, 0)");
    bctx.fillStyle = bgrad;
    bctx.fillRect(0, 0, 256, 256);

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      tiltX = ((e.clientX - r.left) / r.width - 0.5) * 0.3;
      tiltY = ((e.clientY - r.top) / r.height - 0.5) * 0.25;
      cursor.x = e.clientX - r.left;
      cursor.y = e.clientY - r.top;
    };
    const onLeave = () => {
      cursor.x = -9999;
      cursor.y = -9999;
    };
    const onScroll = () => {
      const p = Math.min(1, Math.max(0, window.scrollY / 800));
      targetTighten = 1 - p * 0.32;
    };
    window.addEventListener("pointermove", onMove);
    document.addEventListener("pointerleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });

    const draw = (now) => {
      t += 1;
      curX += (tiltX - curX) * 0.04;
      curY += (tiltY - curY) * 0.04;
      curTighten += (targetTighten - curTighten) * 0.05;

      const k = Math.min(1, (now - introStart) / INTRO);
      const e = 1 - Math.pow(1 - k, 3);
      const extraRot = (1 - e) * 2.4;
      const zoom = 1 + (1 - e) * 0.45;
      const driftY = (1 - e) * 36;
      const fadeIn = Math.min(1, k * 2.4);

      const rot = Math.sin(t * 0.00035) * 0.45 + curX + extraRot;
      const ax = curY + Math.sin(t * 0.0006) * 0.14;
      const cosA = Math.cos(ax);
      const sinA = Math.sin(ax);
      const cx = w / 2;
      const cy = h / 2;
      const S = Math.min(w, h) * 0.34 * curTighten;

      ctx.clearRect(0, 0, w, h);

      const pulse = Math.pow(Math.max(0, Math.sin(t * 0.017)), 3);
      if (pulse > 0.01) {
        const bs = Math.min(w, h) * (0.52 + 0.14 * pulse) * curTighten;
        ctx.globalAlpha = pulse * 0.4 * fadeIn;
        ctx.drawImage(bloomSprite, cx - bs, cy - bs + driftY * 0.5, bs * 2, bs * 2);
        ctx.globalAlpha = 1;
      }

      const pts = [];

      for (let i = 0; i < N; i++) {
        const p = particles[i];
        p.phase += p.speed;
        const th = p.phase;
        const x = Math.sin(th);
        const y = Math.sin(th) * Math.cos(th) * 1.15;
        const z = Math.cos(2 * th) * 0.4;

        const y1 = y * cosA - z * sinA;
        const z1 = y * sinA + z * cosA;
        const x1 = x * Math.cos(rot) - z1 * Math.sin(rot);
        const z2 = x * Math.sin(rot) + z1 * Math.cos(rot);

        const s = 2.3 / (2.3 - z2 * 0.7);
        const px = cx + x1 * S * s * zoom;
        const py = cy + y1 * S * s * zoom + driftY;
        pts.push([px, py]);

        const dx = cursor.x - px;
        const dy = cursor.y - py;
        const d = Math.sqrt(dx * dx + dy * dy);
        const R = S * 0.5;
        const target = cursor.x === -9999 ? 0 : Math.max(0, 1 - d / R);
        p.glow += (target - p.glow) * 0.08;

        const mix = 0.5 + 0.5 * Math.sin(th);
        const tr = trail[i];
        tr.push([px, py, s]);
        if (tr.length > 14) tr.shift();

        if (i % 2 === 0) {
          for (let kk = 0; kk < tr.length - 1; kk++) {
            const fade = (kk / tr.length) * 0.22 * tr[kk][2] * fadeIn;
            ctx.globalAlpha = fade;
            const rad = p.r * 1.6 * tr[kk][2];
            ctx.drawImage(sprite, tr[kk][0] - rad * 2, tr[kk][1] - rad * 2, rad * 4, rad * 4);
          }
        }

        const twk = 0.6 + 0.4 * Math.sin(t * 0.002 * p.ts + p.tw);
        const depth = Math.max(0.4, Math.min(1.4, s));
        const isComet = i === 0;
        const rad = (isComet ? p.r * 3.2 : p.r * 2.6) * depth * (1 + p.glow * 0.7);
        ctx.globalAlpha = Math.min(
          1,
          twk * (isComet ? 1 : 0.8) * depth * (1 + p.glow * 1.6) * fadeIn,
        );
        ctx.drawImage(sprite, px - rad * 2, py - rad * 2, rad * 4, rad * 4);
        ctx.globalAlpha = 1;
      }

      ctx.lineWidth = 0.6;
      for (let i = 0; i < N; i += 3) {
        for (let j = i + 3; j < Math.min(i + 21, N); j += 3) {
          const dx = pts[i][0] - pts[j][0];
          const dy = pts[i][1] - pts[j][1];
          const d2 = dx * dx + dy * dy;
          const max = S * 0.3;
          if (d2 < max * max) {
            const a = (1 - Math.sqrt(d2) / max) * 0.12 * fadeIn;
            ctx.strokeStyle = `rgba(90, 120, 255, ${a})`;
            ctx.beginPath();
            ctx.moveTo(pts[i][0], pts[i][1]);
            ctx.lineTo(pts[j][0], pts[j][1]);
            ctx.stroke();
          }
        }
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
